import { Log } from '@apparel/core/log';
import { ContainedButton } from '@components/Buttons';
import { Trans } from '@components/Trans';
import { uuid } from '@lib/random';
import { createStyled } from '@lib/style/createStyled';
import { unitStrict } from '@lib/style/unit';
import { cleanProps } from '@lib/utils';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import { DialogContext, DialogProvider } from '@packages/dialog/createDialog/context';
import { call } from '@packages/functions';
import { createMemo } from '@packages/react';
import React, { CSSProperties, MutableRefObject, useCallback, useContext, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

export { DialogActions } from '../DialogActions';
export { DialogContent } from '../DialogContent';
export { DialogHeader } from '../DialogHeader';
export { DialogLoading } from '../DialogLoading';
export { DialogTitle } from '../DialogTitle';

export interface DialogApi {
    setOpen(state: boolean): void;

    cancel(): void;

    close(): void;

    open(): void;

    show(): void;

    toggle(): void;

    isOpen: boolean;
}

const POSITIONS = {
    top: css`
        & {
            display: flex;
            justify-content: center;
            align-content: flex-start;
            align-items: flex-start;
        }
    `,
    'top-left': css`
        & {
            display: flex;
            justify-content: flex-start;
            align-content: flex-start;
            align-items: flex-start;
        }
    `,
    'top-right': css`
        & {
            display: flex;
            justify-content: flex-end;
            align-content: flex-start;
            align-items: flex-start;
        }
    `,

    center: css`
        & {
            display: flex;
            justify-content: center;
            align-content: center;
            align-items: center;
        }
    `,
    'center-left': css`
        & {
            display: flex;
            justify-content: flex-start;
            align-content: center;
            align-items: center;
        }
    `,
    'center-right': css`
        & {
            display: flex;
            justify-content: flex-end;
            align-content: center;
            align-items: center;
        }
    `,

    bottom: css`
        & {
            display: flex;
            justify-content: center;
            align-content: flex-end;
            align-items: flex-end;
        }
    `,
    'bottom-left': css`
        & {
            display: flex;
            justify-content: flex-start;
            align-content: flex-end;
            align-items: flex-end;
        }
    `,
    'bottom-right': css`
        & {
            display: flex;
            justify-content: flex-end;
            align-content: flex-end;
            align-items: flex-end;
        }
    `,
};

type DialogActionType = Promise<void | null | {
    open?: boolean;
    cancel?: boolean;
}>;

export interface DialogPropsInterface extends Omit<DialogProps, 'maxWidth' | 'open' | 'children'> {
    onOpen?: () => void | DialogActionType;
    onClose?: () => void | DialogActionType;
    onExit?: () => void | DialogActionType;
    noToggle?: boolean;
    maxWidth?: string | number;
    minWidth?: string | number;
    width?: string | number;
    maxHeight?: string | number;
    minHeight?: string | number;
    borderRadius?: string | number;
    height?: string | number;
    boxShadow?: string;
    toggleText?: string;
    id?: string;
    toggleElement?: any;
    dataProps?: string[];
    children?: any;
    loading?: boolean;
    open?: boolean;
    apiRef?: MutableRefObject<DialogApi | null>;

    margin?: CSSProperties['margin'];
    position?: keyof typeof POSITIONS;
}

export const createDialog = function <T = any>(factoryProps$: DialogPropsInterface) {
    const { open, ...factoryProps } = factoryProps$;

    return (Wrapper: any) => {
        return createMemo<T & Exclude<DialogPropsInterface, keyof T>>(
            ({ children, open, ...rest }) => (
                <DialogProvider>
                    <DialogBase {...{ margin: 24, ...factoryProps, ...rest, Wrapper }}>{children}</DialogBase>
                </DialogProvider>
            ),
            {
                displayName: `core(Dialog)(${Wrapper.displayName || 'Unknown'})`,
            },
        );
    };
};

const DialogBase = createMemo<DialogPropsInterface & { Wrapper: any }>(
    (props) => {
        const {
            onOpen,
            onClose,
            toggleText,
            toggleElement,
            noToggle,
            children,
            id,
            Wrapper,
            dataProps = [],
            apiRef,
            loading: loading$,
            ...rest
        } = props;

        const [state, { setOpen }] = useContext(DialogContext);

        const internalID = `${id || uuid()}`;
        const createID = (key) => `dialog_${internalID}_${key}`;

        const log = Log.channel('DialogBase');

        /**
         * Set loading & open to true, then call `onOpen` event
         */
        const handleOpen = useCallback(
            async (event) => {
                log.debug('event.handleOpen');
                setOpen(true);

                const data = await call(onOpen, { event });

                if (data && data.cancel) {
                    setOpen(false);
                }
            },
            [setOpen, onOpen],
        );

        const handleClose = useCallback(
            async (...args) => {
                log.debug('event.handleClose');
                const data = await call(onClose, ...args);

                if (!data || data.cancel) {
                    setOpen(false);
                }
            },
            [setOpen, onClose],
        );

        const api$ = useMemo(
            () => ({
                setOpen: async (value: any) => {
                    log.debug('api.call setOpen');
                    await call(value ? onOpen : onClose);
                    setOpen(value);
                },
                show: async () => {
                    log.debug('api.call show');
                    await call(onOpen);
                    setOpen(true);
                },
                open: async () => {
                    log.debug('api.call open');
                    await call(onOpen);
                    setOpen(true);
                },
                cancel: async () => {
                    log.debug('api.call cancel');
                    await call(onClose);
                    setOpen(false);
                },
                close: async () => {
                    log.debug('api.call close');
                    await call(onClose);
                    setOpen(false);
                },
                toggle: async () => {
                    const value = !state.get('open');
                    log.debug('api.call toggle');
                    await call(value ? onOpen : onClose);
                    setOpen(value);
                },
                isOpen: state.get('open'),
            }),
            [setOpen, state, state.get('open')],
        );

        useEffect(() => {
            if (apiRef) {
                apiRef.current = api$;
            }
        }, [apiRef]);

        /**
         * Create toggle base on input data, if there's a child passed
         * we will use it as toggle else we will create our own.
         *
         * @param el
         * @returns {*}
         */
        const toggle$ = useMemo(() => {
            const el = children || toggleElement;

            if (el) {
                if (React.isValidElement(el)) {
                    return React.Children.map(el, (item) => {
                        const { onClick, ...props } = item.props as any;

                        props.onClick = (e) => {
                            call([handleOpen, onClick], e);
                        };

                        return {
                            ...item,
                            props,
                        };
                    });
                } else {
                    const Toggle = el;
                    return <Toggle onClick={handleOpen} />;
                }
            }

            if (noToggle) {
                return null;
            }

            return (
                <Trans onClick={handleOpen} component={ContainedButton}>
                    {toggleText || 'Open Dialog'}
                </Trans>
            );
        }, [children, toggleElement]);

        cleanProps(rest, dataProps);

        return (
            <>
                {toggle$}
                <Style
                    open={state.get('open')}
                    onClose={handleClose}
                    aria-labelledby={createID('title')}
                    classes={{ paper: 'dialog__content' }}
                    TransitionProps={{ unmountOnExit: true }}
                    {...rest}
                >
                    <Wrapper
                        api={api$}
                        {...dataProps.reduce(
                            (bag, prop) => ({
                                ...bag,
                                [prop]: props[prop],
                            }),
                            {},
                        )}
                    />
                </Style>
            </>
        );
    },
    {
        displayName: 'DialogBase',
    },
);

const _prop = (p, prop) => (props) =>
    props.hasOwnProperty(p)
        ? css`
              ${prop}: ${unitStrict(props[p])};
          `
        : '';

const Style = styled(
    createStyled(Dialog, 'margin,position,minWidth,maxWidth,width,minHeight,maxHeight,height,borderRadius,boxShadow'),
)`
    && {
        .MuiDialog-container {
            ${({ position }) => POSITIONS[position]}
        }

        .dialog {
            &__content {
                ${_prop('borderRadius', 'border-radius')}
                ${_prop('margin', 'margin')}
                ${_prop('maxWidth', 'max-width')}
                ${_prop('minWidth', 'min-width')}
                ${_prop('width', 'width')}
                ${_prop('maxHeight', 'max-height')}
                ${_prop('minHeight', 'min-height')}
                ${_prop('height', 'height')}
                ${_prop('boxShadow', 'box-shadow')}
            }
        }
    }
`;
